import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IVirtuousSyncSummary, VirtuousIntegration } from '@feathr/blackbox';
import { customFieldHubField, defaultPersonAttributes } from '@feathr/blackbox';
import type {
  IIntegrationUIConfig,
  IIntegrationUIFieldOption,
  IIntegrationUIToggleSection,
} from '@feathr/components';
import { IntegrationStatRow, SyncRuleSection } from '@feathr/components';
import type { ICrmMappingDraft } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.hooks';
import { useCrmContactMappings } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.hooks';
import CrmMappingTable from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/CrmMappingTable';

import { CONTACT_DOWNSYNC_KEY, PERSON_UPSYNC_KEY } from '../virtuousConfig';
import { VIRTUOUS_ICON } from '../virtuousIcon';
import {
  getActivityValues,
  getSyncSummaryStats,
  stageDownsyncActivity,
  stageUpsyncActivity,
} from '../VirtuousIntegrationPage.utils';
import VirtuousTabContent from '../VirtuousTabContent';

const DEFAULT_FIELD_IDS: ReadonlySet<string> = new Set(defaultPersonAttributes.map(({ id }) => id));
/** Feathr fields magpie's Virtuous catalog has no counterpart for. */
const UNMAPPABLE_FIELD_IDS: ReadonlySet<string> = new Set(['address', 'address.timezone']);

/** magpie names a custom field by its u_key, which is the option's name. */
export function toHubFieldOptions(
  options: readonly IIntegrationUIFieldOption[],
): readonly IIntegrationUIFieldOption[] {
  return options
    .filter((option) => !UNMAPPABLE_FIELD_IDS.has(option.id))
    .map((option) =>
      DEFAULT_FIELD_IDS.has(option.id)
        ? option
        : { ...option, id: customFieldHubField(option.name) },
    );
}

interface IProps {
  integration: VirtuousIntegration;
  /** The page config. The table reads its `mapping` region. */
  config: IIntegrationUIConfig;
  /** The config's toggle sections for this tab, in display order around the table. */
  sectionsAbove: readonly IIntegrationUIToggleSection[];
  sectionsBelow: readonly IIntegrationUIToggleSection[];
  draft: ICrmMappingDraft;
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
  /** `undefined` while loading, `null` if the load failed. */
  syncSummary?: IVirtuousSyncSummary | null;
}

function VirtuousMappingsTab({
  config,
  disabledReason,
  draft,
  integration,
  sectionsAbove,
  sectionsBelow,
  syncSummary,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  // This tab owns the mapping load; the page's draft reads the same store.
  const store = useCrmContactMappings(integration);
  const values = getActivityValues({
    downsync_activities: integration.get('downsync_activities'),
    upsync_activities: integration.get('upsync_activities'),
  });

  function handleChange(key: string, value: boolean): void {
    if (key === CONTACT_DOWNSYNC_KEY) {
      stageDownsyncActivity(integration, 'contacts', value);
    } else if (key === PERSON_UPSYNC_KEY) {
      stageUpsyncActivity(integration, 'people', value);
    }
  }

  function renderSection(section: IIntegrationUIToggleSection): JSX.Element {
    return (
      <SyncRuleSection
        crmIcon={VIRTUOUS_ICON}
        disabledReason={disabledReason}
        key={section.key}
        name={`virtuous-${section.key}`}
        onChange={handleChange}
        section={section}
        values={values}
      />
    );
  }

  return (
    <VirtuousTabContent
      inactiveLabel={t('Connect Virtuous to choose which fields sync.')}
      integration={integration}
      isWide={true}
    >
      {syncSummary !== undefined && (
        <IntegrationStatRow
          name={'virtuous-sync-summary'}
          stats={getSyncSummaryStats(syncSummary, t)}
        />
      )}
      {sectionsAbove.map(renderSection)}
      <CrmMappingTable
        config={config}
        crmIcon={VIRTUOUS_ICON}
        draft={draft}
        errorName={'virtuous-mappings-error'}
        mapFeathrFieldOptions={toHubFieldOptions}
        name={'virtuous-mapping'}
        store={store}
      />
      {sectionsBelow.map(renderSection)}
    </VirtuousTabContent>
  );
}

export default observer(VirtuousMappingsTab);
